import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Facebook, Linkedin, Rss, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

interface FeedItem {
  platform: string;
  source: string;
  title: string;
  link: string;
  pubDate: string;
  description?: string;
}

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case "facebook":
      return <Facebook className="h-4 w-4" />;
    case "linkedin":
      return <Linkedin className="h-4 w-4" />;
    case "rss":
      return <Rss className="h-4 w-4" />;
    default:
      return <Rss className="h-4 w-4" />;
  }
};

const getPlatformColor = (platform: string) => {
  switch (platform) {
    case "facebook":
      return "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
    case "linkedin":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-500";
    case "xing":
      return "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400";
    case "rss":
      return "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Gerade eben';
  if (diffInHours < 24) return `Vor ${diffInHours}h`;
  if (diffInHours < 48) return 'Gestern';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

// Decode HTML entities - handles both named and numeric entities
const decodeHtmlEntities = (text: string): string => {
  // First pass: Replace common named HTML entities manually
  const entityMap: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&nbsp;': ' ',
    '&Ouml;': 'Ö',
    '&ouml;': 'ö',
    '&Auml;': 'Ä',
    '&auml;': 'ä',
    '&Uuml;': 'Ü',
    '&uuml;': 'ü',
    '&szlig;': 'ß',
    '&euro;': '€',
    '&ndash;': '–',
    '&mdash;': '—',
    '&laquo;': '«',
    '&raquo;': '»',
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
  };
  
  let decoded = text;
  
  // Replace all known entities
  Object.entries(entityMap).forEach(([entity, char]) => {
    decoded = decoded.replace(new RegExp(entity, 'g'), char);
  });
  
  // Handle numeric entities like &#246; or &#xF6;
  decoded = decoded.replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)));
  decoded = decoded.replace(/&#x([0-9A-Fa-f]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  
  return decoded;
};

// Clean title by removing source prefixes, location codes, and excessive punctuation
const cleanTitle = (title: string, source: string) => {
  let cleaned = decodeHtmlEntities(title)
    // Remove source prefix
    .replace(new RegExp(`^${source}\\s*[-–:]\\s*`, 'i'), '')
    .replace(/^RSS\s*[-–:]?\s*/i, '')
    // Remove location codes like "KÖLN INNENSTADT" or similar uppercase location patterns at start
    .replace(/^[A-ZÄÖÜ]{2,}(?:\s+[A-ZÄÖÜ]{2,})*[,;:\s]*/g, '')
    // Remove pipe separators and everything after
    .replace(/\s*\|\s*.*$/, '')
    // Remove multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
  
  // Capitalize first letter
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }
  
  return cleaned || decodeHtmlEntities(title);
};

export const SocialMediaFeed = () => {
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('fetch-social-feeds');
        
        if (error) throw error;
        
        setFeedItems(data || []);
      } catch (error) {
        console.error('Error fetching feeds:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeeds();
    const interval = setInterval(fetchFeeds, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          Aktuelle Branchennews
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Neueste Meldungen aus der Immobilienwirtschaft
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {isLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded-lg border border-border">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </>
        ) : feedItems.length > 0 ? (
          <>
            {feedItems.slice(0, 5).map((item, index) => (
              <article
                key={index}
                className="p-4 rounded-lg border border-border bg-card hover:border-primary/30 transition-all duration-200 overflow-hidden"
              >
                <div className="flex flex-col items-center text-center gap-3">
                  <span className={`p-2 rounded-full ${getPlatformColor(item.platform)}`}>
                    {getPlatformIcon(item.platform)}
                  </span>
                  <div className="w-full">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-xs font-medium text-primary">{item.source}</span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{formatDate(item.pubDate)}</span>
                    </div>
                    <h4 className="text-sm font-medium text-foreground line-clamp-2 mb-3 break-words">
                      {cleanTitle(item.title, item.source)}
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 text-xs"
                      asChild
                    >
                      <a 
                        href={item.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Angebot ansehen
                        <ArrowRight className="ml-1.5 h-3 w-3" />
                      </a>
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </>
        ) : (
          <div className="text-center py-6">
            <Rss className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Keine aktuellen Beiträge verfügbar
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};